/**
 * Visual appearance of a single turn bar in the navigation rail.
 */
interface TurnBarAppearance {
  /** Bar length in pixels — swells on hover to form the waveform. */
  width: number;
  /** CSS background value (solid token or gradient). */
  background: string;
}

const BAR_SHORT = 12;
const BAR_NEAR_2 = 18;
const BAR_NEAR_1 = 26;
const BAR_PEAK = 36;

const COLOR_REST = 'var(--sky-dark)';
const COLOR_ACTIVE = 'var(--secondary-light)';
const COLOR_NEAR_2 = 'var(--secondary-lighter)';
const COLOR_NEAR_1 = 'linear-gradient(90deg, var(--secondary-light), var(--purple-light))';
const COLOR_PEAK = 'linear-gradient(90deg, var(--secondary-base), var(--purple-base))';

/**
 * Computes the width and background for a turn bar based on the current
 * hover and scroll state.
 *
 * When a bar is hovered, the rail forms a waveform centered on it: the hovered
 * bar is the longest with a purple gradient, its immediate neighbors are
 * shorter, second neighbors shorter still, and everything else stays a short
 * grey tick. When nothing is hovered, every bar keeps the same short length so
 * the rail reads as uniform; the turn currently scrolled into view
 * ({@link activeIndex}) is tinted (colour only, not length) as a subtle
 * "you are here" indicator.
 *
 * @param {number} index - The bar's own turn index
 * @param {number} hoveredIndex - Index of the hovered bar, or -1 if none
 * @param {number} activeIndex - Index of the turn currently scrolled into view
 * @returns {TurnBarAppearance} The width and background to render for this bar
 */
const getTurnBarAppearance = (index: number, hoveredIndex: number, activeIndex: number): TurnBarAppearance => {
  if (hoveredIndex >= 0) {
    const distance = Math.abs(index - hoveredIndex);
    switch (distance) {
      case 0:
        return { width: BAR_PEAK, background: COLOR_PEAK };
      case 1:
        return { width: BAR_NEAR_1, background: COLOR_NEAR_1 };
      case 2:
        return { width: BAR_NEAR_2, background: COLOR_NEAR_2 };
      default:
        return { width: BAR_SHORT, background: COLOR_REST };
    }
  }

  if (index === activeIndex) return { width: BAR_SHORT, background: COLOR_ACTIVE };
  return { width: BAR_SHORT, background: COLOR_REST };
};

export { getTurnBarAppearance };
export type { TurnBarAppearance };
